var _a;
import { __assign, __read, __rest } from "tslib";
/* eslint-disable no-console */
import * as React from 'react';
import { createElement, } from 'react';
import { systemToComponent } from '@virtuoso.dev/react-urx';
import { compose, connect, distinctUntilChanged, getValue, map, noop, pipe, publish, statefulStream, statefulStreamFromEmitter, stream, subscribe, system, tup, withLatestFrom, } from '@virtuoso.dev/urx';
import useChangedListContentsSizes from './hooks/useChangedChildSizes';
import useIsomorphicLayoutEffect from './hooks/useIsomorphicLayoutEffect';
import useScrollTop from './hooks/useScrollTop';
import useSize from './hooks/useSize';
import useWindowViewportRectRef from './hooks/useWindowViewportRect';
import { listSystem } from './listSystem';
import conditionalFlushSync from './utils/conditionalFlushSync';
import { correctItemSize } from './utils/correctItemSize';
import { positionStickyCssValue } from './utils/positionStickyCssValue';
export function identity(value) {
    return value;
}
var listComponentPropsSystem = system(function () {
    var itemContent = statefulStream(function (index) { return "Item " + index; });
    var context = statefulStream(null);
    var groupContent = statefulStream(function (index) { return "Group " + index; });
    var components = statefulStream({});
    var computeItemKey = statefulStream(identity);
    var headerFooterTag = statefulStream('div');
    var scrollerRef = statefulStream(noop);
    var distinctProp = function (propName, defaultValue) {
        if (defaultValue === void 0) { defaultValue = null; }
        return statefulStreamFromEmitter(pipe(components, map(function (components) { return components[propName]; }), distinctUntilChanged()), defaultValue);
    };
    return {
        context: context,
        itemContent: itemContent,
        groupContent: groupContent,
        components: components,
        computeItemKey: computeItemKey,
        headerFooterTag: headerFooterTag,
        scrollerRef: scrollerRef,
        FooterComponent: distinctProp('Footer'),
        HeaderComponent: distinctProp('Header'),
        TopItemListComponent: distinctProp('TopItemList'),
        ListComponent: distinctProp('List', 'div'),
        ItemComponent: distinctProp('Item', 'div'),
        GroupComponent: distinctProp('Group', 'div'),
        ScrollerComponent: distinctProp('Scroller', 'div'),
        EmptyPlaceholder: distinctProp('EmptyPlaceholder'),
        ScrollSeekPlaceholder: distinctProp('ScrollSeekPlaceholder'),
    };
});
export function addDeprecatedAlias(prop, message) {
    var alias = stream();
    subscribe(alias, function () {
        return console.warn("react-virtuoso: You are using a deprecated property. " + message, 'color: red;', 'color: inherit;', 'color: blue;');
    });
    connect(alias, prop);
    return alias;
}
var combinedSystem = system(function (_a) {
    var _b = __read(_a, 2), listSystem = _b[0], propsSystem = _b[1];
    var deprecatedProps = {
        item: addDeprecatedAlias(propsSystem.itemContent, 'Rename the %citem%c prop to %citemContent.'),
        group: addDeprecatedAlias(propsSystem.groupContent, 'Rename the %cgroup%c prop to %cgroupContent.'),
        topItems: addDeprecatedAlias(listSystem.topItemCount, 'Rename the %ctopItems%c prop to %ctopItemCount.'),
        itemHeight: addDeprecatedAlias(listSystem.fixedItemHeight, 'Rename the %citemHeight%c prop to %cfixedItemHeight.'),
        scrollingStateChange: addDeprecatedAlias(listSystem.isScrolling, 'Rename the %cscrollingStateChange%c prop to %cisScrolling.'),
        adjustForPrependedItems: stream(),
        maxHeightCacheSize: stream(),
        footer: stream(),
        header: stream(),
        HeaderContainer: stream(),
        FooterContainer: stream(),
        ItemContainer: stream(),
        ScrollContainer: stream(),
        GroupContainer: stream(),
        ListContainer: stream(),
        emptyComponent: stream(),
        scrollSeek: stream(),
    };
    subscribe(deprecatedProps.adjustForPrependedItems, function () {
        console.warn("react-virtuoso: adjustForPrependedItems is no longer supported. Use the firstItemIndex property instead - https://virtuoso.dev/prepend-items.", 'color: red;', 'color: inherit;', 'color: blue;');
    });
    subscribe(deprecatedProps.maxHeightCacheSize, function () {
        console.warn("react-virtuoso: maxHeightCacheSize is no longer necessary. Setting it has no effect - remove it from your code.");
    });
    subscribe(deprecatedProps.HeaderContainer, function () {
        console.warn("react-virtuoso: HeaderContainer is deprecated. Use headerFooterTag if you want to change the wrapper of the header component and pass components.Header to change its contents.");
    });
    subscribe(deprecatedProps.FooterContainer, function () {
        console.warn("react-virtuoso: FooterContainer is deprecated. Use headerFooterTag if you want to change the wrapper of the footer component and pass components.Footer to change its contents.");
    });
    function deprecateComponentProp(stream, componentName, propName) {
        connect(pipe(stream, withLatestFrom(propsSystem.components), map(function (_a) {
            var _b;
            var _c = __read(_a, 2), comp = _c[0], components = _c[1];
            console.warn("react-virtuoso: " + propName + " property is deprecated. Pass components." + componentName + " instead.");
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            return __assign(__assign({}, components), (_b = {}, _b[componentName] = comp, _b));
        })), propsSystem.components);
    }
    subscribe(deprecatedProps.scrollSeek, function (_a) {
        var placeholder = _a.placeholder, config = __rest(_a, ["placeholder"]);
        console.warn("react-virtuoso: scrollSeek property is deprecated. Pass scrollSeekConfiguration and specify the placeholder in components.ScrollSeekPlaceholder instead.");
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        publish(propsSystem.components, __assign(__assign({}, getValue(propsSystem.components)), { ScrollSeekPlaceholder: placeholder }));
        publish(listSystem.scrollSeekConfiguration, config);
    });
    deprecateComponentProp(deprecatedProps.footer, 'Footer', 'footer');
    deprecateComponentProp(deprecatedProps.header, 'Header', 'header');
    deprecateComponentProp(deprecatedProps.ItemContainer, 'Item', 'ItemContainer');
    deprecateComponentProp(deprecatedProps.ListContainer, 'List', 'ListContainer');
    deprecateComponentProp(deprecatedProps.ScrollContainer, 'Scroller', 'ScrollContainer');
    deprecateComponentProp(deprecatedProps.emptyComponent, 'EmptyPlaceholder', 'emptyComponent');
    deprecateComponentProp(deprecatedProps.GroupContainer, 'Group', 'GroupContainer');
    return __assign(__assign(__assign({}, listSystem), propsSystem), deprecatedProps);
}, tup(listSystem, listComponentPropsSystem));
var DefaultScrollSeekPlaceholder = function (_a) {
    var height = _a.height;
    return (React.createElement("div", { style: { height: height } }));
};
var GROUP_STYLE = {
    position: positionStickyCssValue(),
    zIndex: 1,
    overflowAnchor: 'none',
};
var ITEM_STYLE = { overflowAnchor: 'none' };
export var Items = React.memo(function VirtuosoItems(_a) {
    var _b = _a.showTopList, showTopList = _b === void 0 ? false : _b;
    var listState = useEmitterValue('listState');
    var _c = __read(React.useState(0), 2), deviation = _c[0], setDeviation = _c[1];
    var react18ConcurrentRendering = useEmitterValue('react18ConcurrentRendering');
    useEmitter('deviation', function (value) {
        if (deviation !== value) {
            conditionalFlushSync(react18ConcurrentRendering)(function () {
                return setDeviation(value);
            });
        }
    });
    var sizeRanges = usePublisher('sizeRanges');
    var useWindowScroll = useEmitterValue('useWindowScroll');
    var customScrollParent = useEmitterValue('customScrollParent');
    var windowScrollContainerStateCallback = usePublisher('windowScrollContainerState');
    var _scrollContainerStateCallback = usePublisher('scrollContainerState');
    var scrollContainerStateCallback = customScrollParent || useWindowScroll
        ? windowScrollContainerStateCallback
        : _scrollContainerStateCallback;
    var itemContent = useEmitterValue('itemContent');
    var context = useEmitterValue('context');
    var groupContent = useEmitterValue('groupContent');
    var trackItemSizes = useEmitterValue('trackItemSizes');
    var itemSize = useEmitterValue('itemSize');
    var log = useEmitterValue('log');
    var ref = useChangedListContentsSizes(sizeRanges, itemSize, trackItemSizes, showTopList ? noop : scrollContainerStateCallback, log, customScrollParent);
    var EmptyPlaceholder = useEmitterValue('EmptyPlaceholder');
    var ScrollSeekPlaceholder = useEmitterValue('ScrollSeekPlaceholder') || DefaultScrollSeekPlaceholder;
    var ListComponent = useEmitterValue('ListComponent');
    var ItemComponent = useEmitterValue('ItemComponent');
    var GroupComponent = useEmitterValue('GroupComponent');
    var computeItemKey = useEmitterValue('computeItemKey');
    var isSeeking = useEmitterValue('isSeeking');
    var hasGroups = useEmitterValue('groupIndices').length > 0;
    var paddingTopAddition = useEmitterValue('paddingTopAddition');
    var firstItemIndex = useEmitterValue('firstItemIndex');
    var statefulTotalCount = useEmitterValue('statefulTotalCount');
    var containerStyle = showTopList
        ? {}
        : {
            boxSizing: 'border-box',
            paddingTop: listState.offsetTop + paddingTopAddition,
            paddingBottom: listState.offsetBottom,
            marginTop: deviation,
        };
    if (!showTopList && statefulTotalCount === 0 && EmptyPlaceholder) {
        return createElement(EmptyPlaceholder, contextPropIfNotDomElement(EmptyPlaceholder, context));
    }
    return createElement(ListComponent, __assign(__assign({}, contextPropIfNotDomElement(ListComponent, context)), { ref: ref, style: containerStyle, 'data-test-id': showTopList
            ? 'virtuoso-top-item-list'
            : 'virtuoso-item-list' }), (showTopList ? listState.topItems : listState.items).map(function (item) {
        var index = item.originalIndex;
        var key = computeItemKey(index + firstItemIndex, item.data, context);
        if (isSeeking) {
            return createElement(ScrollSeekPlaceholder, __assign(__assign(__assign({}, contextPropIfNotDomElement(ScrollSeekPlaceholder, context)), { key: key, index: item.index, height: item.size, type: item.type || 'item' }), (item.type === 'group' ? {} : { groupIndex: item.groupIndex })));
        }
        if (item.type === 'group') {
            return createElement(GroupComponent, __assign(__assign({}, contextPropIfNotDomElement(GroupComponent, context)), { key: key, 'data-index': index, 'data-known-size': item.size, 'data-item-index': item.index, style: GROUP_STYLE }), groupContent(item.index));
        }
        return createElement(ItemComponent, __assign(__assign({}, contextPropIfNotDomElement(ItemComponent, context)), { key: key, 'data-index': index, 'data-known-size': item.size, 'data-item-index': item.index, 'data-item-group-index': item.groupIndex, style: ITEM_STYLE }), hasGroups
            ? itemContent(item.index, item.groupIndex, item.data, context)
            : itemContent(item.index, item.data, context));
    }));
});
export var scrollerStyle = {
    height: '100%',
    outline: 'none',
    overflowY: 'auto',
    position: 'relative',
    WebkitOverflowScrolling: 'touch',
};
export var viewportStyle = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
};
var topItemListStyle = {
    width: '100%',
    position: positionStickyCssValue(),
    top: 0,
};
export function contextPropIfNotDomElement(element, context) {
    if (typeof element === 'string') {
        return undefined;
    }
    return { context: context };
}
var Header = React.memo(function VirtuosoHeader() {
    var Header = useEmitterValue('HeaderComponent');
    var headerHeight = usePublisher('headerHeight');
    var headerFooterTag = useEmitterValue('headerFooterTag');
    var ref = useSize(function (el) { return headerHeight(correctItemSize(el, 'height')); });
    var context = useEmitterValue('context');
    return Header
        ? createElement(headerFooterTag, { ref: ref }, createElement(Header, contextPropIfNotDomElement(Header, context)))
        : null;
});
var Footer = React.memo(function VirtuosoFooter() {
    var Footer = useEmitterValue('FooterComponent');
    var footerHeight = usePublisher('footerHeight');
    var headerFooterTag = useEmitterValue('headerFooterTag');
    var ref = useSize(function (el) { return footerHeight(correctItemSize(el, 'height')); });
    var context = useEmitterValue('context');
    return Footer
        ? createElement(headerFooterTag, { ref: ref }, createElement(Footer, contextPropIfNotDomElement(Footer, context)))
        : null;
});
export function buildScroller(_a) {
    var usePublisher = _a.usePublisher, useEmitter = _a.useEmitter, useEmitterValue = _a.useEmitterValue;
    var Scroller = React.memo(function VirtuosoScroller(_a) {
        var style = _a.style, children = _a.children, props = __rest(_a, ["style", "children"]);
        var scrollContainerStateCallback = usePublisher('scrollContainerState');
        var ScrollerComponent = useEmitterValue('ScrollerComponent');
        var smoothScrollTargetReached = usePublisher('smoothScrollTargetReached');
        var scrollerRefCallback = useEmitterValue('scrollerRef');
        var context = useEmitterValue('context');
        var _b = useScrollTop(scrollContainerStateCallback, smoothScrollTargetReached, ScrollerComponent, scrollerRefCallback), scrollerRef = _b.scrollerRef, scrollByCallback = _b.scrollByCallback, scrollToCallback = _b.scrollToCallback;
        useEmitter('scrollTo', scrollToCallback);
        useEmitter('scrollBy', scrollByCallback);
        return createElement(ScrollerComponent, __assign(__assign({ ref: scrollerRef, style: __assign(__assign({}, scrollerStyle), style), 'data-test-id': 'virtuoso-scroller', 'data-virtuoso-scroller': true, tabIndex: 0 }, props), contextPropIfNotDomElement(ScrollerComponent, context)), children);
    });
    return Scroller;
}
export function buildWindowScroller(_a) {
    var usePublisher = _a.usePublisher, useEmitter = _a.useEmitter, useEmitterValue = _a.useEmitterValue;
    var Scroller = React.memo(function VirtuosoWindowScroller(_a) {
        var style = _a.style, children = _a.children, props = __rest(_a, ["style", "children"]);
        var scrollContainerStateCallback = usePublisher('windowScrollContainerState');
        var ScrollerComponent = useEmitterValue('ScrollerComponent');
        var smoothScrollTargetReached = usePublisher('smoothScrollTargetReached');
        var totalListHeight = useEmitterValue('totalListHeight');
        var deviation = useEmitterValue('deviation');
        var customScrollParent = useEmitterValue('customScrollParent');
        var context = useEmitterValue('context');
        var _b = useScrollTop(scrollContainerStateCallback, smoothScrollTargetReached, ScrollerComponent, noop, customScrollParent), scrollerRef = _b.scrollerRef, scrollByCallback = _b.scrollByCallback, scrollToCallback = _b.scrollToCallback;
        useIsomorphicLayoutEffect(function () {
            scrollerRef.current = customScrollParent ? customScrollParent : window;
            return function () {
                scrollerRef.current = null;
            };
        }, [scrollerRef, customScrollParent]);
        useEmitter('windowScrollTo', scrollToCallback);
        useEmitter('scrollBy', scrollByCallback);
        return createElement(ScrollerComponent, __assign(__assign({ style: __assign(__assign({ position: 'relative' }, style), (totalListHeight !== 0
                ? { height: totalListHeight + deviation }
                : {})), 'data-virtuoso-scroller': true }, props), contextPropIfNotDomElement(ScrollerComponent, context)), children);
    });
    return Scroller;
}
var Viewport = function (_a) {
    var children = _a.children;
    var viewportHeight = usePublisher('viewportHeight');
    var viewportRef = useSize(compose(viewportHeight, function (el) { return correctItemSize(el, 'height'); }));
    return (React.createElement("div", { style: viewportStyle, ref: viewportRef, "data-viewport-type": "element" }, children));
};
var WindowViewport = function (_a) {
    var children = _a.children;
    var windowViewportRect = usePublisher('windowViewportRect');
    var customScrollParent = useEmitterValue('customScrollParent');
    var viewportRef = useWindowViewportRectRef(windowViewportRect, customScrollParent);
    return (React.createElement("div", { ref: viewportRef, style: viewportStyle, "data-viewport-type": "window" }, children));
};
var TopItemListContainer = function (_a) {
    var children = _a.children;
    var TopItemList = useEmitterValue('TopItemListComponent');
    var headerHeight = useEmitterValue('headerHeight');
    var style = __assign(__assign({}, topItemListStyle), { marginTop: headerHeight + "px" });
    var context = useEmitterValue('context');
    return createElement(TopItemList || 'div', { style: style, context: context }, children);
};
var ListRoot = React.memo(function VirtuosoRoot(props) {
    var useWindowScroll = useEmitterValue('useWindowScroll');
    var showTopList = useEmitterValue('topItemsIndexes').length > 0;
    var customScrollParent = useEmitterValue('customScrollParent');
    var TheScroller = customScrollParent || useWindowScroll ? WindowScroller : Scroller;
    var TheViewport = customScrollParent || useWindowScroll ? WindowViewport : Viewport;
    return (React.createElement(TheScroller, __assign({}, props),
        React.createElement(TheViewport, null,
            React.createElement(Header, null),
            React.createElement(Items, null),
            React.createElement(Footer, null)),
        showTopList && (React.createElement(TopItemListContainer, null,
            React.createElement(Items, { showTopList: true })))));
});
export var List = (_a = systemToComponent(combinedSystem, {
    required: {},
    optional: {
        context: 'context',
        followOutput: 'followOutput',
        firstItemIndex: 'firstItemIndex',
        itemContent: 'itemContent',
        groupContent: 'groupContent',
        overscan: 'overscan',
        increaseViewportBy: 'increaseViewportBy',
        totalCount: 'totalCount',
        topItemCount: 'topItemCount',
        initialTopMostItemIndex: 'initialTopMostItemIndex',
        components: 'components',
        groupCounts: 'groupCounts',
        atBottomThreshold: 'atBottomThreshold',
        computeItemKey: 'computeItemKey',
        defaultItemHeight: 'defaultItemHeight',
        fixedItemHeight: 'fixedItemHeight',
        itemSize: 'itemSize',
        scrollSeekConfiguration: 'scrollSeekConfiguration',
        headerFooterTag: 'headerFooterTag',
        data: 'data',
        initialItemCount: 'initialItemCount',
        initialScrollTop: 'initialScrollTop',
        alignToBottom: 'alignToBottom',
        useWindowScroll: 'useWindowScroll',
        customScrollParent: 'customScrollParent',
        scrollerRef: 'scrollerRef',
        logLevel: 'logLevel',
        react18ConcurrentRendering: 'react18ConcurrentRendering',
        // deprecated
        item: 'item',
        group: 'group',
        topItems: 'topItems',
        itemHeight: 'itemHeight',
        scrollingStateChange: 'scrollingStateChange',
        maxHeightCacheSize: 'maxHeightCacheSize',
        footer: 'footer',
        header: 'header',
        ItemContainer: 'ItemContainer',
        ScrollContainer: 'ScrollContainer',
        ListContainer: 'ListContainer',
        GroupContainer: 'GroupContainer',
        emptyComponent: 'emptyComponent',
        HeaderContainer: 'HeaderContainer',
        FooterContainer: 'FooterContainer',
        scrollSeek: 'scrollSeek',
    },
    methods: {
        scrollToIndex: 'scrollToIndex',
        scrollIntoView: 'scrollIntoView',
        scrollTo: 'scrollTo',
        scrollBy: 'scrollBy',
        adjustForPrependedItems: 'adjustForPrependedItems',
    },
    events: {
        isScrolling: 'isScrolling',
        endReached: 'endReached',
        startReached: 'startReached',
        rangeChanged: 'rangeChanged',
        atBottomStateChange: 'atBottomStateChange',
        atTopStateChange: 'atTopStateChange',
        totalListHeightChanged: 'totalListHeightChanged',
        itemsRendered: 'itemsRendered',
        groupIndices: 'groupIndices',
    },
}, ListRoot), _a.Component), usePublisher = _a.usePublisher, useEmitterValue = _a.useEmitterValue, useEmitter = _a.useEmitter;
var Scroller = buildScroller({ usePublisher: usePublisher, useEmitterValue: useEmitterValue, useEmitter: useEmitter });
var WindowScroller = buildWindowScroller({
    usePublisher: usePublisher,
    useEmitterValue: useEmitterValue,
    useEmitter: useEmitter,
});
